import React, { ReactElement, useEffect, useMemo } from "react";
import { HashRouter as Router } from "react-router-dom";

import { AbstractBaseApi } from "@common/api/v1.1/actions/AbstractBaseApi";
import { Content } from "./Content";
import { Messages } from "./Messages";
import { Navigation } from "./Navigation";
import { Sidebar } from "./Sidebar";
import { AuthState, ChildrenArgs, ITAP_BOT_EMAIL, ITAP_USER_EMAIL, JobState } from "./types";
import { useWorkbenchState } from "./useWorkbenchState";

import "bootstrap/dist/css/bootstrap.min.css";

import { SSEProvider } from "react-hooks-sse";
import "./styles.scss";

type Props = Readonly<{
  blockingStep: string;
  children: (args: ChildrenArgs) => ReactElement;
  docs?: React.ReactElement;
  loadRoles: Array<string>;
  review: boolean;
  settings: ReactElement | undefined;
  workbenchTitle: string;
}>;

export const Workbench = ({
  blockingStep,
  children,
  docs,
  loadRoles,
  review,
  settings,
  workbenchTitle,
}: Props) => {
  const [state, actions] = useWorkbenchState(blockingStep);
  const wbTitle = useMemo(() => {
    if (review) {
      return `${workbenchTitle} (Review)`;
    }
    return workbenchTitle;
  }, [review, workbenchTitle]);
  const jobTitle = useMemo(() => {
    if (state.job.state === JobState.ready && state.job.job.name && state.job.job.name.length > 0) {
      return state.job.job.name;
    }
    return null;
  }, [state.job.state, wbTitle]);
  useEffect(() => {
    if (jobTitle && jobTitle.length > 0) {
      document.title = `${wbTitle} - ${jobTitle}`;
    } else {
      document.title = wbTitle;
    }
  }, [jobTitle, wbTitle]);
  useEffect(() => {
    if (state.auth.state === AuthState.loggedIn && state.auth.user.email !== ITAP_USER_EMAIL && !state.auth.user.email.endsWith(ITAP_BOT_EMAIL)) {
      actions.showMessage(
        <span>
          Logged in as <strong>{state.auth.user.name}</strong>.
        </span>,
        "Login successful",
        3,
        "success",
      );
    }
  }, [state.auth.state]);
  const visible =
    state.sidebarVisible ||
    state.auth.state === AuthState.notLoggedIn ||
    state.auth.state === AuthState.loggingInByForm;
  let main = (
    <main>
      <Content
        actions={actions}
        loadRoles={loadRoles}
        render={children}
        review={review}
        sidebarVisible={visible}
        state={state}
      />
      {!review && (
        <Sidebar
          actions={actions}
          blockingStep={blockingStep}
          customSettings={settings}
          docs={docs}
          state={state}
        />
      )}
    </main>
  );
  let isItapUser = false;
  if (state.auth.state === AuthState.loggedIn) {    
    const eventStreamEndPoint = `${AbstractBaseApi.getApiBaseUri()}/users/${encodeURIComponent(
      state.auth.user.id,
    )}/events?token=${encodeURIComponent(state.auth.token)}`;
    isItapUser = state.auth.user.email === ITAP_USER_EMAIL || state.auth.user.email.endsWith(ITAP_BOT_EMAIL);
    main = <SSEProvider endpoint={eventStreamEndPoint}>{main}</SSEProvider>;
  }
  return (
    <>
      <Router>
        <>
          {!isItapUser && <Navigation
            jobTitle={jobTitle}
            review={review}
            showProductionAlert={
              state.job.state === JobState.ready && state.job.job.use_for_training
            }
            title={wbTitle}
            toggleSidebar={actions.toggleSidebar}
          />}
          {main}
        </>
      </Router>
      <Messages hideMessage={actions.hideMessage} messages={state.messages} />
    </>
  );
};
Workbench.displayName = "Workbench";
